import React, { useEffect, useState, useRef } from 'react';
import { ShieldHalfIcon, UserIcon, LockIcon, ShieldCheckIcon, ArrowLeftIcon } from 'lucide-react';
interface LoginPageProps {
  onLogin: () => void;
}
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [loginStep, setLoginStep] = useState<'credentials' | 'otp'>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [otpValues, setOtpValues] = useState<string[]>(Array(6).fill(''));
  const [otpError, setOtpError] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loginStep === 'otp' && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setIsResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [loginStep, resendTimer]);
  const handleCredentialsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (username && password) {
      setError('');
      setLoginStep('otp');
      setResendTimer(60);
      setIsResendDisabled(true);
      setOtpValues(Array(6).fill(''));
      setOtpError('');
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
    } else {
      setError('Username dan password wajib diisi.');
    }
  };
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtpValues = [...otpValues];
    newOtpValues[index] = value.slice(-1);
    setOtpValues(newOtpValues);
    setOtpError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otpValues[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOtpValues = [...otpValues];
        newOtpValues[index] = '';
        setOtpValues(newOtpValues);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newOtpValues = [...otpValues];
      for (let i = 0; i < pastedData.length; i++) {
        newOtpValues[i] = pastedData[i];
      }
      setOtpValues(newOtpValues);
      const nextIndex = Math.min(pastedData.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };
  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const otpCode = otpValues.join('');
    if (otpCode.length === 6) {
      onLogin();
    } else {
      setOtpError('Masukkan 6 digit kode OTP.');
    }
  };
  const handleResendCode = () => {
    setResendTimer(60);
    setIsResendDisabled(true);
    setOtpError('');
  };
  const handleBackToLogin = () => {
    setLoginStep('credentials');
    setOtpValues(Array(6).fill(''));
    setOtpError('');
  };
  const maskedEmail = username.includes('@') ? username.replace(/(.{2})(.*)(?=@)/, (match, p1, p2) => p1 + '*'.repeat(p2.length)) : `${username.slice(0, 2)}****@ross.mil.id`;
  const isOtpComplete = otpValues.every(val => val !== '');
  return <div className="min-h-screen flex bg-[#0B0F1A] font-sans text-[#E2E8F0]" data-id="element-701">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[60%] bg-gradient-to-br from-[#070A12] to-[#0B0F1A] flex-col justify-center items-center p-12 relative overflow-hidden border-r border-[#1E3A5F]" data-id="element-702">
        <div className="absolute inset-0 cyber-grid opacity-20 pointer-events-none" data-id="element-703"></div>
        <div className="absolute inset-0 scanline opacity-30 pointer-events-none" data-id="element-704"></div>

        <div className="absolute top-8 left-8 flex items-center space-x-2" data-id="element-705">
          <span className="w-2 h-2 rounded-full bg-[#00FF88] glow-green animate-pulse" data-id="element-706"></span>
          <span className="text-xs text-[#00FF88] uppercase tracking-wider" data-id="element-707">
            System Online
          </span>
        </div>

        <div className="absolute bottom-8 left-8 flex items-center space-x-2 text-[#94A3B8]" data-id="element-708">
          <LockIcon className="w-3 h-3" data-id="element-709" />
          <span className="text-xs uppercase tracking-wider" data-id="element-710">
            Encrypted Connection
          </span>
        </div>

        <div className="relative z-10 flex flex-col items-center text-center max-w-2xl" data-id="element-711">
          <div className="w-28 h-28 bg-[#0B0F1A]/50 rounded-full flex items-center justify-center mb-8 backdrop-blur-sm border-2 border-[#00D4FF]/40 glow-cyan-strong relative" data-id="element-712">
            <div className="absolute inset-0 rounded-full border border-[#00D4FF]/20 animate-ping opacity-20" data-id="element-713"></div>
            <ShieldHalfIcon className="w-14 h-14 text-[#00D4FF]" data-id="element-714" />
          </div>
          <h1 className="text-5xl font-bold tracking-wide mb-4 text-[#E2E8F0] drop-shadow-[0_0_10px_rgba(0,212,255,0.3)] uppercase" data-id="element-715">
            ROSS
          </h1>
          <h2 className="text-xl font-bold text-[#00D4FF] mb-6 uppercase tracking-wide" data-id="element-716">
            Ruang Operasi Siber Strategis
          </h2>
          <p className="text-base text-[#94A3B8] leading-relaxed max-w-lg" data-id="element-717">
            Dashboard terintegrasi untuk pemantauan status, progres pelaksanaan,
            dokumentasi kegiatan, dan rekapitulasi informasi operasional secara
            real-time.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-[40%] flex flex-col justify-center items-center p-8 sm:p-12 relative" data-id="element-718">
        <div className="absolute inset-0 cyber-grid opacity-10 pointer-events-none" data-id="element-719"></div>

        <div className="w-full max-w-md bg-[#111827] rounded shadow-2xl border border-[#1E3A5F] p-8 relative z-10" data-id="element-720">
          {loginStep === 'credentials' ? <>
              <div className="text-center mb-8" data-id="element-721">
                <div className="lg:hidden w-16 h-16 bg-[#0B0F1A] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#00D4FF]/40 glow-cyan" data-id="element-722">
                  <ShieldHalfIcon className="w-8 h-8 text-[#00D4FF]" data-id="element-723" />
                </div>
                <h2 className="text-2xl font-bold text-[#E2E8F0] uppercase tracking-wide" data-id="element-724">
                  Akses Sistem
                </h2>
                <p className="text-sm text-[#94A3B8] mt-2" data-id="element-725">
                  AUTHORIZATION REQUIRED
                </p>
              </div>

              {error && <div className="mb-6 p-3 bg-[#FF3B3B]/10 border border-[#FF3B3B]/30 text-[#FF3B3B] rounded text-sm text-center glow-red" data-id="element-726">
                  {error}
                </div>}

              <form onSubmit={handleCredentialsSubmit} className="space-y-5" data-id="element-727">
                <div data-id="element-728">
                  <label className="block text-xs font-semibold text-[#94A3B8] mb-1.5" data-id="element-729">
                    Username / Email
                  </label>
                  <div className="relative" data-id="element-730">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-731">
                      <UserIcon className="h-5 w-5 text-[#00D4FF]/50" data-id="element-732" />
                    </div>
                    <input type="text" value={username} onChange={e => setUsername(e.target.value)} className="block w-full pl-10 pr-3 py-2.5 border border-[#1E3A5F] rounded focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] sm:text-sm bg-[#0B0F1A] text-[#E2E8F0] placeholder-[#475569] transition-all" placeholder="Masukkan username" data-id="element-733" />
                  </div>
                </div>

                <div data-id="element-734">
                  <label className="block text-xs font-semibold text-[#94A3B8] mb-1.5" data-id="element-735">
                    Password
                  </label>
                  <div className="relative" data-id="element-736">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-737">
                      <LockIcon className="h-5 w-5 text-[#00D4FF]/50" data-id="element-738" />
                    </div>
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-10 pr-10 py-2.5 border border-[#1E3A5F] rounded focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] sm:text-sm bg-[#0B0F1A] text-[#E2E8F0] placeholder-[#475569] transition-all" placeholder="••••••••" data-id="element-739" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-xs font-medium text-[#00D4FF]/70 hover:text-[#00D4FF]" data-id="element-740">
                      {showPassword ? 'Sembunyikan' : 'Lihat'}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2" data-id="element-741">
                  <div className="flex items-center" data-id="element-742">
                    <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-[#00D4FF] focus:ring-[#00D4FF] border-[#1E3A5F] bg-[#0B0F1A] rounded" data-id="element-743" />
                    <label htmlFor="remember-me" className="ml-2 block text-xs text-[#94A3B8]" data-id="element-744">
                      Ingat Saya
                    </label>
                  </div>

                  <div className="text-xs" data-id="element-745">
                    <a href="#" className="font-medium text-[#00D4FF]/70 hover:text-[#00D4FF]" data-id="element-746">
                      Lupa Password?
                    </a>
                  </div>
                </div>

                <button type="submit" className="w-full flex justify-center py-3 px-4 border border-transparent rounded shadow-sm text-sm font-bold text-[#0B0F1A] bg-[#00D4FF] hover:bg-[#00D4FF]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0B0F1A] focus:ring-[#00D4FF] transition-all mt-6 uppercase tracking-wider glow-cyan" data-id="element-747">
                  MASUK
                </button>
              </form>
            </> : <>
              <button onClick={handleBackToLogin} className="flex items-center text-xs font-medium text-[#94A3B8] hover:text-[#00D4FF] mb-6 transition-colors" data-id="element-748">
                <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-749" />
                Kembali
              </button>

              <div className="text-center mb-8" data-id="element-750">
                <div className="w-16 h-16 bg-[#00D4FF]/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#00D4FF]/30 glow-cyan" data-id="element-751">
                  <ShieldCheckIcon className="w-8 h-8 text-[#00D4FF]" data-id="element-752" />
                </div>
                <h2 className="text-2xl font-bold text-[#E2E8F0] uppercase tracking-wide" data-id="element-753">
                  Verifikasi Dua Langkah
                </h2>
                <p className="text-sm text-[#94A3B8] mt-2" data-id="element-754">
                  Kode OTP telah dikirim ke email yang terdaftar
                </p>
                <p className="text-sm font-medium text-[#00D4FF] mt-1" data-id="element-755">
                  {maskedEmail}
                </p>
              </div>

              {otpError && <div className="mb-6 p-3 bg-[#FF3B3B]/10 border border-[#FF3B3B]/30 text-[#FF3B3B] rounded text-sm text-center glow-red" data-id="element-756">
                  {otpError}
                </div>}

              <form onSubmit={handleOtpSubmit} className="space-y-6" data-id="element-757">
                <div className="flex justify-between gap-2" data-id="element-758">
                  {otpValues.map((value, index) => <input key={index} ref={el => inputRefs.current[index] = el} type="text" inputMode="numeric" maxLength={1} value={value} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} onPaste={handleOtpPaste} className="w-12 h-14 text-center text-2xl font-bold border border-[#1E3A5F] rounded focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0] transition-all font-mono" data-id="element-759" />)}
                </div>

                <button type="submit" disabled={!isOtpComplete} className={`w-full flex justify-center py-3 px-4 border border-transparent rounded shadow-sm text-sm font-bold uppercase tracking-wider transition-all mt-6 ${isOtpComplete ? 'text-[#0B0F1A] bg-[#00D4FF] hover:bg-[#00D4FF]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0B0F1A] focus:ring-[#00D4FF] glow-cyan' : 'text-[#94A3B8] bg-[#1E3A5F]/50 cursor-not-allowed'}`} data-id="element-760">
                  VERIFIKASI
                </button>
              </form>

              <div className="mt-8 text-center text-sm" data-id="element-761">
                <p className="text-[#94A3B8]" data-id="element-762">
                  Belum menerima kode?{' '}
                  {isResendDisabled ? <span className="text-[#E2E8F0]" data-id="element-763">
                      Kirim ulang dalam {resendTimer}s
                    </span> : <button onClick={handleResendCode} className="text-[#00D4FF] hover:text-[#00D4FF]/80 hover:underline font-medium" data-id="element-764">
                      Kirim Ulang Kode
                    </button>}
                </p>
              </div>
            </>}
        </div>

        <div className="mt-12 text-center relative z-10" data-id="element-765">
          <p className="text-xs text-[#FF3B3B]/60 uppercase tracking-widest font-bold" data-id="element-766">
            CLASSIFIED — INTERNAL USE ONLY
          </p>
          <p className="text-xs text-[#94A3B8] mt-1" data-id="element-767">
            Ruang Operasi Siber Strategis
          </p>
          <p className="text-xs text-[#64748B] mt-2 font-mono" data-id="element-768">v1.0.0_SECURE</p>
        </div>
      </div>
    </div>;
}